import {
	faChevronRight,
	faFileContract,
	faIndustryWindows,
	faStore,
} from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { alpha, Chip, Paper, Stack, Typography } from '@mui/material'
import { numToCurrency } from '@utils/stringHelpers'

import ContractChip from '../ContractChip'
import DirectDealRejectedChip from '../DirectDealRejectedChip'
import RecommendationChip from '../RecommendationChip'

type Props = {
	id: Id
	termSetName: string
	topLevelCustomerName: string
	manufacturerName: string
	selected?: boolean
	recommended: boolean
	onContract: boolean
	requiresPriceImpl: boolean
	directDealRejected: boolean
	numProducts: number
	annualTotalSavings: number
	onClick: () => void
}

export default function ContractCard({
	id,
	termSetName,
	topLevelCustomerName,
	manufacturerName,
	selected = false,
	recommended,
	onContract,
	requiresPriceImpl,
	directDealRejected,
	numProducts,
	annualTotalSavings,
	onClick,
}: Props) {
	const testId = termSetName.replace(/\s+/g, '-').toLowerCase().slice(0, 20).replace(/-+$/, '')

	return (
		<Paper
			elevation={1}
			data-testid={`contract-loading-card-id-${id}`}
			onClick={onClick}
			sx={{
				width: '100%',
				height: 'fit-content',
				borderRadius: 2,
				border: selected ? '2px solid' : '1px solid',
				borderColor: selected ? (theme) => theme.palette.info.main : 'divider',
				bgcolor: selected ? (theme) => alpha(theme.palette.info.main, 0.08) : 'background.paper',
				cursor: 'pointer',
				boxShadow: '0 0.25rem 0.25rem 0 rgba(0, 0, 0, 0.25)',
				p: 2,
			}}
		>
			<Stack spacing={0.5}>
				<Stack direction="row" justifyContent="space-between" alignItems="flex-start">
					<Stack direction="row" spacing={1} alignItems="center">
						<FontAwesomeIcon icon={faFileContract} style={{ fontSize: '1.25rem' }} />
						<Typography
							variant="body2"
							fontWeight="bold"
							data-testid={`contract-loading-card-term-set-${testId}`}
						>
							{termSetName}
						</Typography>
					</Stack>
					<FontAwesomeIcon icon={faChevronRight} />
				</Stack>

				<Stack direction="row" spacing={1} alignItems="center">
					<FontAwesomeIcon icon={faIndustryWindows} />
					<Typography
						variant="caption"
						color="text.secondary"
						data-testid={`contract-loading-card-manufacturer-${testId}`}
					>
						{manufacturerName}
					</Typography>
				</Stack>

				<Stack direction="row" spacing={1} alignItems="center">
					<FontAwesomeIcon icon={faStore} />
					<Typography
						variant="caption"
						color="text.secondary"
						data-testid={`contract-loading-card-customer-${testId}`}
					>
						{topLevelCustomerName}
					</Typography>
				</Stack>

				<Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
					<RecommendationChip
						recommended={recommended}
						data-testid={`contract-loading-card-recommendation-${testId}`}
					/>
					<ContractChip
						onContract={onContract}
						data-testid={`contract-loading-card-on-contract-${testId}`}
					/>
					{requiresPriceImpl && (
						<Chip
							label="Requires Price Impl"
							data-testid={`contract-loading-card-requires-price-impl-${testId}`}
							sx={{
								width: 'fit-content',
								color: 'warning.dark',
								bgcolor: (theme) => alpha(theme.palette.warning.dark, 0.08),
							}}
						/>
					)}
					{directDealRejected && (
						<DirectDealRejectedChip
							data-testid={`contract-loading-card-direct-deal-rejected-${testId}`}
						/>
					)}
				</Stack>

				<Stack direction="row" spacing={8} pl={0.5}>
					<Stack>
						<Typography variant="caption" color="text.secondary" fontWeight="bold">
							# of Products
						</Typography>
						<Typography
							variant="body2"
							fontWeight="bold"
							data-testid={`contract-loading-card-num-products-${testId}`}
						>
							{numProducts}
						</Typography>
					</Stack>

					<Stack>
						<Typography variant="caption" color="text.secondary" fontWeight="bold">
							Total Savings Amount
						</Typography>
						<Typography
							variant="body2"
							fontWeight="bold"
							color={annualTotalSavings > 0 ? 'success' : 'error'}
							data-testid={`contract-loading-card-annual-total-savings-${testId}`}
						>
							{numToCurrency(annualTotalSavings, 'USD', 0)}
						</Typography>
					</Stack>
				</Stack>
			</Stack>
		</Paper>
	)
}
